import React, { useRef, useEffect, useState } from "react";
import {
  Download,
  RotateCcw,
  Plus,
  Check,
  X,
  Palette,
  Undo,
} from "lucide-react";
import SignaturePad from "signature_pad";
import { useSearchParams } from "react-router-dom";
import { TemplateAPI } from "../api/all.ts";
import { Toast, Modal, Button, Card } from "@douyinfe/semi-ui";
import "./SignaturePad.css";
import { IconAlertTriangle } from "@douyinfe/semi-icons";

export default function SignatureApp() {
  const [searchParams] = useSearchParams();
  const keyValue = searchParams.get("key");
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [penColor, setPenColor] = useState("#000000");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [penWidth, setPenWidth] = useState(2);
  const [paramValue, setParamValue] = useState("");
  const [recordData, setRecordData] = useState({
    templateType: "",
    meegoUserId: "",
    projectKey: "",
    workItemTypeKey: "",
    operateType: "",
    workItemList: [],
  });
  const [imgBase64, setImgBase64] = useState("");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [isSubmitted, setIsSubmitted] = useState(false);


  useEffect(() => {
    getSelectWriteSignatureRecord();
  }, []);

  // 初始化签名板
  useEffect(() => {
    const handleOrientationChange = () => {
      const newIsPortrait = window.matchMedia(
        "(orientation: portrait)"
      ).matches;
      setIsPortrait(newIsPortrait);
      window.location.reload();
      resizeCanvas();
    };

    window.addEventListener("orientationchange", handleOrientationChange);
    window.addEventListener("resize", handleOrientationChange);
    resizeCanvas();

    return () => {
      window.removeEventListener("orientationchange", handleOrientationChange);
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, [penColor, penWidth, keyValue]);
  const resizeCanvas = () => {
    if (canvasRef.current && SignaturePad) {
      // 设置画布尺寸
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      canvas.getContext("2d").scale(ratio, ratio);
      canvas.style.border = "none";
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      // 创建签名板实例
      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: "rgba(0, 0, 0, 0)",
        penColor: penColor,
        minWidth: penWidth * 0.5,
        maxWidth: penWidth * 1.5,
        throttle: 16,
        minDistance: 5,
        velocityFilterWeight: 0.7,
        dotSize: 0,
      });

      // 监听窗口大小变化
      const resizeCanvas = () => {
        const data = signaturePadRef.current.toData();
        const rect = canvas.getBoundingClientRect();
        const ratio = Math.max(window.devicePixelRatio || 1, 1);

        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        canvas.border = "none";
        canvas.style.border = "none";
        canvas.getContext("2d").scale(ratio, ratio);
        canvas.style.width = rect.width + "px";
        canvas.style.height = rect.height + "px";

        signaturePadRef.current.clear();
        signaturePadRef.current.fromData(data);
      };

      window.addEventListener("resize", resizeCanvas);

      return () => {
        window.removeEventListener("resize", resizeCanvas);
        if (signaturePadRef.current) {
          signaturePadRef.current.off();
        }
      };
    }
  };

  const getSelectWriteSignatureRecord = async () => {
    if (!keyValue) {
      Toast.warning({
        content: "请使用三峡行云扫一扫打开",
        duration: 10,
        showClose: false,
      });
      return;
    }

    setParamValue(keyValue);
    const res = await TemplateAPI.selectWriteSignatureRecord({
      key: keyValue,
    });
    setRecordData({
      ...JSON.parse(res.data.requestJson),
    });

    const newSignature = {
      id: Date.now(),
      dataUrl: "data:image/png;base64," + res.data.imgBase64,
      // svgData: svgData,
      timestamp: new Date().toLocaleString("zh-CN"),
      penColor: penColor,
      penWidth: penWidth,
    };
    setSignatures((prev) => [...prev, newSignature]);
  };

  const saveCurrentSignature = () => {
    if (signaturePadRef.current.isEmpty()) {
      Toast.error("请先在画板上签名！");
      return;
    }
    if (isSubmitted) {
      Toast.error({
        content: "已提交，不能再次提交,请去PC端查看",
        duration: 3,
        showClose: false,
      });
      return;
    }
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {

      Modal.confirm({
        title: "确认签名？",
        okButtonProps: {
          style: {
            backgroundColor: '#005bac',
            borderColor: '#005bac',
            color: '#fff'
          }
        },
        icon: <IconAlertTriangle style={{ color: 'var(--semi-color-warning)' }} />,
        content: "",
        onOk: async () => {
          const dataUrl = signaturePadRef.current.toDataURL("image/png");
          const base64Data = dataUrl.split(",")[1];
          setImgBase64(dataUrl);
          const params = {
            templateType: recordData.templateType,
            meegoUserId: recordData.meegoUserId, // 应从store获取
            projectKey: recordData.projectKey,
            workItemTypeKey: recordData.workItemTypeKey,
            operateType: recordData.operateType,
            workItemList: recordData.workItemList,
            key: keyValue,
            base64Sign: base64Data,
            nodeId: recordData.nodeId,
          };
          const res = await TemplateAPI.batchESignature(params);
          if (res.code === 0) {
            // 禁用所有操作
            setIsSubmitted(true);
            // 禁用画布绘制
            signaturePadRef.current.off();
          }
        },
      });
    }
  };

  const clearCanvas = () => {
    if (isSubmitted) {
      Toast.error({
        content: "已提交，不能清除,请去PC端查看",
        duration: 3,
        showClose: false,
      });
      return;
    }
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const undoLastStroke = () => {
    if (isSubmitted) {
      Toast.error({
        content: "已提交，不能撤销,请去PC端查看",
        duration: 3,
        showClose: false,
      });
      return;
    }
    if (signaturePadRef.current) {
      const data = signaturePadRef.current.toData();
      if (data.length > 0) {
        data.pop(); // 移除最后一笔
        signaturePadRef.current.fromData(data);
      }
    }
  };

  const selectSignature = (signature) => {
    setSelectedSignature(signature);
  };

  const deleteSignature = (id) => {
    setSignatures((prev) => prev.filter((sig) => sig.id !== id));
    if (selectedSignature?.id === id) {
      setSelectedSignature(null);
    }
  };

  const downloadSignature = (signature, format = "png") => {
    const link = document.createElement("a");

    if (format === "svg") {
      const blob = new Blob([signature.svgData], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      link.download = `signature-${new Date().getTime()}.svg`;
      link.href = url;
    } else {
      link.download = `signature-${new Date().getTime()}.png`;
      link.href = signature.dataUrl;
    }

    link.click();

    if (format === "svg") {
      URL.revokeObjectURL(link.href);
    }
  };

  const loadSignatureToCanvas = (signature) => {
    console.log(canvasRef);
    console.log(signaturePadRef.current);
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setTimeout(() => {
        signaturePadRef.current.fromDataURL(signature.dataUrl);
      }, 0);
    }
  };

  const colors = [
    "#000000",
    "#1e40af",
    "#dc2626",
    "#059669",
    "#7c3aed",
    "#ea580c",
  ];
  const widths = [1, 2, 3, 4, 5];

  return keyValue&&(
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto">
        {/* 标题 */}
        {!isPortrait && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 签名画板区域 */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-2">


                {/* 画布 */}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <div style={{ width: "60%" }}>
                    <Card className="CardName">
                      <div className="" style={{ border: "none" }}>
                        <canvas
                          ref={canvasRef}
                          className="w-full h-64 bg-white  cursor-crosshair "
                          style={{ touchAction: "none" }}
                        />
                      </div>
                    </Card>
                    <p className="text-sm text-gray-500 text-center mt-2">
                      在上方区域签名
                    </p>
                  </div>

                  <div style={{ width: "40%" }}>
                    <Card className="CardNameRight">
                      <div className="">
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="text-xl font-semibold text-gray-800"></h2>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={undoLastStroke}
                              className="flex items-center gap-2 px-2 py-1 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                              title="撤销上一笔"
                            >
                              撤销
                            </button>

                            <button
                              onClick={clearCanvas}
                              className="flex items-center gap-2 px-2 py-1 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              清除
                            </button>

                            <button
                              onClick={saveCurrentSignature}
                              className="flex items-center gap-2 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              style={{ background: '#005bac' }}
                            >
                              提交
                            </button>
                          </div>
                        </div>
                        {signatures.length === 0 ? (
                          <div className="text-center py-8">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <Plus className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500">暂无保存的签名</p>
                            <p className="text-sm text-gray-400">
                              在画板上签名后点击提交
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3 max-h-96 overflow-y-auto">
                            {signatures.map((signature) => (
                              <div
                                key={signature.id}
                                className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${selectedSignature?.id === signature.id
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300"
                                  }`}
                                onClick={() => selectSignature(signature)}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm text-gray-600">
                                    {/* {signature.timestamp} */}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      theme="borderless"
                                      type="primary"
                                      style={{ marginRight: 15, color: '#005bac' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        loadSignatureToCanvas(signature);
                                      }}
                                    >
                                      使用
                                    </Button>
                                    <Button
                                      theme="borderless"
                                      type="danger"
                                      style={{ marginRight: 8 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteSignature(signature.id);
                                      }}
                                    >
                                      删除
                                    </Button>
                                  </div>
                                </div>
                                <div className="bg-white border rounded p-2">
                                  <img
                                    src={signature.dataUrl}
                                    alt="签名"
                                    style={{ height: "5.2rem" }}
                                    className="w-full  object-contain"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>


          </div>
        )}

        {isPortrait && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
            {/* 签名画板区域 */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-2">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800"></h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={undoLastStroke}
                      className="flex items-center gap-2 px-2 py-1 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="撤销上一笔"
                    >
                      撤销
                    </button>

                    <button
                      onClick={clearCanvas}
                      className="flex items-center gap-2 px-2 py-1 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      清除
                    </button>

                    <button
                      onClick={saveCurrentSignature}
                      className="flex items-center gap-2 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      style={{ background: '#005bac' }}
                    >
                      提交
                    </button>
                  </div>
                </div>

                {/* 画布 */}
                <Card className="CardName">
                  <div className="" style={{ border: "none" }}>
                    <canvas
                      ref={canvasRef}
                      className="w-full h-64 bg-white  cursor-crosshair "
                      style={{ touchAction: "none" }}
                    />
                  </div>
                </Card>
                <p className="text-sm text-gray-500 text-center mt-2">
                  在上方区域签名
                </p>
              </div>
            </div>

            {/* 签名管理区域 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                签名库
              </h2>
              {signatures.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plus className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">暂无保存的签名</p>
                  <p className="text-sm text-gray-400">
                    在画板上签名后点击提交
                  </p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {signatures.map((signature) => (
                    <div
                      key={signature.id}
                      className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${selectedSignature?.id === signature.id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                        }`}
                      onClick={() => selectSignature(signature)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">
                          {/* {signature.timestamp} */}
                        </span>
                        <div className="flex items-center gap-1">
                          <Button
                            theme="borderless"
                            type="primary"
                            style={{ marginRight: 15, color: '#005bac' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              loadSignatureToCanvas(signature);
                            }}
                          >
                            使用
                          </Button>
                          <Button
                            theme="borderless"
                            type="danger"
                            style={{ marginRight: 8, }}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSignature(signature.id);
                            }}
                          >
                            删除
                          </Button>
                        </div>
                      </div>
                      <div className="bg-white border rounded p-2">
                        <img
                          src={signature.dataUrl}
                          alt="签名"
                          className="w-full h-12 object-contain"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
